import { AfterContentInit, Component, DoCheck, ElementRef, HostBinding, Inject, Injector, OnInit } from '@angular/core';
import { ModalButton, ModalButtons, ModalCloseReason, ModalRef } from '@app/core/models/modal-ref';
import { CdkTrapFocus, FocusTrapFactory } from '@angular/cdk/a11y';
import { DOCUMENT } from '@angular/common';

/**
 * Needed to give every modal a unique id
 */
let nextUniqueId = 0;

/**
 * Component that wraps every modal.
 *
 * Defines title, content and buttons.
 * The content can be custom (text or component).
 *
 * Also defines the style of the modals as well as width and height restrictions of the modal
 * (by default the modal uses almost the whole viewport width if necessary).
 *
 * E.g. if the modal's max-width should be restricted, this can be done in a component that is used as modal content.
 *
 * By default, the default button is focused when the modal is opened.
 * To change this, add the attribute "cdkFocusInitial" to the element that shoud be focused.
 */
@Component({
  selector: 'app-generic-modal',
  templateUrl: './generic-modal.component.html',
  styleUrls: ['./generic-modal.component.scss']
})
export class GenericModalComponent implements OnInit, AfterContentInit, DoCheck {
  public ModalButtons = ModalButtons;
  public ModalButton = ModalButton;
  public ModalCloseReason = ModalCloseReason;

  public uniqueId = nextUniqueId++;

  public modalButtons = ModalButtons.None;
  public contentType: 'string' | 'component' = 'string';
  /**
   * Used if content is a string (prevents compiler error when translate pipe is used (string is expected but string|Type<any> given))
   */
  public contentString: string;
  // Only required if contentType is component, injects additional data into the component
  public componentInjector: Injector;

  @HostBinding('attr.role') public get role() { return 'dialog'; }
  @HostBinding('attr.aria-labelledby') public get labelledBy() { return `modal-${this.uniqueId}-title`; }
  @HostBinding('attr.aria-describedby') public get describedBy() { return `modal-${this.uniqueId}-description`; }
  @HostBinding('tabindex') public get tabindex() { return -1; }
  @HostBinding('class') public get modalSizeClass() {
    if (this.modalRef == null) {
      return '';
    }

    switch (this.modalRef.modalConfig.sizing) {
      case 'default':
        return 'default-size';
      case 'pagesize':
        return '';
    }
  }

  /**
   * Ensures that the focus stays inside the modal (when "tabbing" through).
   * If a component is used as modal content, the "cdkFocusInitial" attribute can be added to an element to set the initial focus
   * to this element when the modal is opened. By default, the default button is focused.
   *
   * The instantiation of the CdkTrapFocus here is some sort of a workaround
   * because Directives cannot be added to the host element in another way.
   * Because this workaround is used, we must ensure that all lifecycle functions (ngOnInit, ngDoCheck, ...) of the Directive are called.
   * Also, all dependencies must be manually injected here. If a solution is added to Angualar in future, this should be changed.
   *
   * Issue: https://github.com/angular/angular/issues/8785
   * 
   * 
   * Additional note: Currently, it seems like thet TrapFocus can't keep the focus inside the modal if e.g. a focused button gets disabled.
   * In this case, the body will be focused instead of focusing the modal or so.
   */
  @HostBinding('attr.cdkTrapFocus') cdkTrapFocusDirective = new CdkTrapFocus(this.elementRef, this.focusTrapFactory, this.document);

  constructor(
    public modalRef: ModalRef,
    private injector: Injector,
    private elementRef: ElementRef,
    private focusTrapFactory: FocusTrapFactory,
    @Inject(DOCUMENT) private document: Document) {
    this.modalButtons = this.modalRef.modalConfig.buttons;

    this.updateContent();

    this.modalRef.onContentChange.subscribe(() => {
      this.updateContent();
    });

    this.cdkTrapFocusDirective.autoCapture = true;
  }

  ngOnInit(): void {
  }

  ngAfterContentInit() {
    this.cdkTrapFocusDirective.ngAfterContentInit();
  }

  ngDoCheck() {
    this.cdkTrapFocusDirective.ngDoCheck();
  }

  public isDefaultButton(button: ModalButton) {
    return this.modalRef.modalConfig.defaultButton === button;
  }

  /** Returns true if given button is the default button and the form is invalid */
  public isSubmitButtonAndInvalid(button: ModalButton) {
    return this.modalRef.modalConfig.submitButton === button
      && !this.modalRef.modalForm.valid
      && this.modalRef.modalConfig.blockSubmitOnInvalid;
  }

  public onButtonClick(reason: ModalCloseReason) {
    this.modalRef.close(reason);
  }

  /**
   * Needs to be called if the modal content was changed (especially if (new) content is a component)
   */
  private updateContent() {
    if (typeof this.modalRef.content === 'string') {
      this.contentType = 'string';
      this.contentString = this.modalRef.content;
    } else {
      this.contentType = 'component';

      // Create injector to inject data into the content component if there is any data that can be passed
      if (this.modalRef.modalConfig.staticProviders) {
        this.componentInjector = Injector.create({providers: this.modalRef.modalConfig.staticProviders, parent: this.injector});
      }
    }
  }
}
